import type { RansomwareResponse, RansomwareDailyPoint } from "../api/status";

/**
 * Formats a number cleanly with thousand separators.
 * Safe against NaN, null, and undefined.
 */
export function formatNumber(val?: number | null): string {
  if (val === undefined || val === null || isNaN(val) || !isFinite(val)) return "0";
  return val.toLocaleString("en-US");
}

/**
 * Calculates share percentage (part / total * 100) safely.
 * Returns 0 if total is 0, NaN, or infinite.
 */
export function calculateShare(part?: number | null, total?: number | null, decimals = 1): number {
  if (!part || !total || total <= 0 || isNaN(part) || isNaN(total) || !isFinite(part) || !isFinite(total)) {
    return 0;
  }
  const pct = (part / total) * 100;
  return Number(pct.toFixed(decimals));
}

/**
 * Formats a percentage value cleanly.
 */
export function formatPercent(val?: number | null, decimals = 1): string {
  if (val === undefined || val === null || isNaN(val) || !isFinite(val)) return "0.0%";
  return `${val.toFixed(decimals)}%`;
}

/**
 * Returns human-friendly text for timeframe codes.
 */
export function formatTimeframeLabel(tf?: string): string {
  if (!tf) return "30 days";
  const clean = tf.toLowerCase().strip ? tf.toLowerCase().trim() : tf.toLowerCase();
  switch (clean) {
    case "24h":
    case "1d":
      return "24 hours";
    case "7d":
      return "7 days";
    case "30d":
      return "30 days";
    case "3m":
      return "3 months";
    case "6m":
      return "6 months";
    default:
      return tf;
  }
}

/**
 * Generates a rule-based editorial summary from real computed values.
 * Example: "375 disclosures in the last 30 days across 16 groups. Deadlocks leads with 91 (24.3%). Technology & Software is the most targeted sector (22.7%)."
 */
export function generateEditorialSummary(data: RansomwareResponse): string {
  if (!data || !data.summary || data.summary.attacks === 0) {
    return `No verified ransomware disclosures reported in the last ${formatTimeframeLabel(data?.timeframe)}.`;
  }

  const tfLabel = formatTimeframeLabel(data.timeframe);
  const totalAttacks = data.summary.attacks;
  const totalActors = data.summary.actors || (Array.isArray(data.actors) ? data.actors.length : 0);

  const clauses: string[] = [];

  // Clause 1: Disclosures count and active groups
  if (totalAttacks > 0) {
    if (totalActors > 0) {
      clauses.push(`${formatNumber(totalAttacks)} disclosures in the last ${tfLabel} across ${totalActors} groups`);
    } else {
      clauses.push(`${formatNumber(totalAttacks)} disclosures in the last ${tfLabel}`);
    }
  }

  // Clause 2: Top threat actor and share %
  if (Array.isArray(data.actors) && data.actors.length > 0) {
    const topActor = data.actors[0];
    if (topActor && topActor.name) {
      const actorAttacks = topActor.attacks || 0;
      const share = topActor.share_pct ?? calculateShare(actorAttacks, totalAttacks);
      clauses.push(`${topActor.name} leads with ${formatNumber(actorAttacks)} (${share}%)`);
    }
  }

  // Clause 3: Top targeted sector and share %
  if (data.summary.topSector && data.summary.topSector !== "None") {
    const secName = data.summary.topSector;
    let secPct = data.summary.topSectorPct;
    if (!secPct && data.summary.topSectorCount && totalAttacks > 0) {
      secPct = calculateShare(data.summary.topSectorCount, totalAttacks);
    }
    if (secPct && secPct > 0) {
      clauses.push(`${secName} is the most targeted sector (${secPct}%)`);
    } else {
      clauses.push(`${secName} is the most targeted sector`);
    }
  }

  if (clauses.length === 0) {
    return `Ransomware intelligence briefing for the last ${tfLabel}.`;
  }

  return clauses.join(". ") + ".";
}

/**
 * Calculates a 7-day rolling moving average series for the timeline chart.
 */
export function calculate7DayMovingAverage(daily: RansomwareDailyPoint[]): (number | null)[] {
  if (!Array.isArray(daily) || daily.length === 0) return [];
  const windowSize = 7;
  const ma: (number | null)[] = [];

  for (let i = 0; i < daily.length; i++) {
    const currentVal = typeof daily[i]?.count === "number" ? daily[i].count : 0;
    if (i < windowSize - 1) {
      let sum = 0;
      for (let j = 0; j <= i; j++) {
        sum += typeof daily[j]?.count === "number" ? daily[j].count : 0;
      }
      ma.push(Number((sum / (i + 1)).toFixed(1)));
    } else {
      let sum = 0;
      for (let j = i - windowSize + 1; j <= i; j++) {
        sum += typeof daily[j]?.count === "number" ? daily[j].count : 0;
      }
      ma.push(Number((sum / windowSize).toFixed(1)));
    }
  }

  return ma;
}

/**
 * Finds the peak day in a daily series (highest count, latest date on ties).
 */
export function calculatePeakDay(daily: RansomwareDailyPoint[]): RansomwareDailyPoint | null {
  if (!Array.isArray(daily) || daily.length === 0) return null;
  let peakPoint: RansomwareDailyPoint | null = null;
  let maxCount = -1;

  for (const pt of daily) {
    const cnt = typeof pt?.count === "number" ? pt.count : 0;
    // Tie breaker: latest date in series (pt >= maxCount)
    if (cnt >= maxCount && cnt > 0) {
      maxCount = cnt;
      peakPoint = pt;
    }
  }

  return peakPoint;
}

/**
 * Computes nice, visually pleasing Y-axis tick intervals.
 */
export function getNiceYAxisTicks(maxVal: number): { domain: [number, number]; ticks: number[] } {
  if (!isFinite(maxVal) || maxVal <= 0) {
    return { domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] };
  }

  if (maxVal <= 5) {
    return { domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] };
  }
  if (maxVal <= 10) {
    return { domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] };
  }
  if (maxVal <= 20) {
    return { domain: [0, 20], ticks: [0, 5, 10, 15, 20] };
  }
  if (maxVal <= 50) {
    return { domain: [0, 50], ticks: [0, 10, 20, 30, 40, 50] };
  }
  if (maxVal <= 100) {
    return { domain: [0, 100], ticks: [0, 25, 50, 75, 100] };
  }

  // For maxVal > 100, calculate nice step
  const rawStep = maxVal / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude;
  
  let niceStep = 10;
  if (normalizedStep <= 1) niceStep = 1;
  else if (normalizedStep <= 2) niceStep = 2;
  else if (normalizedStep <= 5) niceStep = 5;
  else niceStep = 10;

  const step = niceStep * magnitude;
  const topTick = Math.ceil(maxVal / step) * step;
  const ticks = [0, step, step * 2, step * 3, topTick];

  return { domain: [0, topTick], ticks };
}
